import { BeziehungZumVn } from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { SparteDruckdaten, SpartenType, Zahlungsart } from '@ps20/bff-models';
import { IAppState } from '@ps20-frontend/apps/checkout-store';

export enum DruckDokumentType {
  BEIBLATT_RECHNUNG,
  BEIBLATT_SEPA,
  INFOPAKET,
  PKB,
  VORSCHLAG,
  JAGDBESTAETIGUNG,
  WEWE,
}

export enum DruckDokumentSparte {
  VWG,
  VHV,
  PH,
  RS,
  THH_HUND,
  THH_PFERD,
  TKV,
  URI,
  GEGE,
  RS_VERMIETUNG,
}

export function spartenTypeToDruckDokumentSparte(spartenType: SpartenType): DruckDokumentSparte {
  return ({
    [SpartenType.WOHNGEBAEUDE]: DruckDokumentSparte.VWG,
    [SpartenType.WOHNGEBAEUDE_V2]: DruckDokumentSparte.VWG,
    [SpartenType.HAUSRAT]: DruckDokumentSparte.VHV,
    [SpartenType.HAUSRAT_V2]: DruckDokumentSparte.VHV,
    [SpartenType.PRIVATHAFTPFLICHT]: DruckDokumentSparte.PH,
    [SpartenType.RECHTSSCHUTZ]: DruckDokumentSparte.RS,
    [SpartenType.UNFALL]: DruckDokumentSparte.URI,
    [SpartenType.TIERHALTERHAFTPFLICHT_HUND]: DruckDokumentSparte.THH_HUND,
    [SpartenType.TIERHALTERHAFTPFLICHT_PFERD]: DruckDokumentSparte.THH_PFERD,
    [SpartenType.TIERKRANKEN]: DruckDokumentSparte.TKV,
    [SpartenType.TIERKRANKEN_V2]: DruckDokumentSparte.TKV,
    [SpartenType.GEGENSTANDSSCHUTZ]: DruckDokumentSparte.GEGE,
    [SpartenType.RECHTSSCHUTZ_VERMIETUNG]: DruckDokumentSparte.RS_VERMIETUNG,
  } as Partial<Record<SpartenType, DruckDokumentSparte>>)[spartenType];
}

export interface DokumentnameBestandteile {
  type: DruckDokumentType;
  spartenName: DruckDokumentSparte;
  vorname: string;
  nachname: string;
  adelsbez?: string;
}

export function beiblattTypeForZahlungsweise(
  zahlungsweise: Zahlungsart,
): DruckDokumentType | undefined {
  switch (zahlungsweise) {
    case Zahlungsart.LASTSCHRIFT:
      return DruckDokumentType.BEIBLATT_SEPA;
    case Zahlungsart.RECHNUNG:
      return DruckDokumentType.BEIBLATT_RECHNUNG;
  }
}

function druckDokumentTypeToBezeichnung(type: DruckDokumentType): string | undefined {
  switch (type) {
    case DruckDokumentType.BEIBLATT_SEPA:
      return 'Beiblatt_zur_Unterschrift_SEPA-Mandat_Privatschutz';
    case DruckDokumentType.BEIBLATT_RECHNUNG:
      return 'Beiblatt_zur_Unterschrift_Privatschutz';
    case DruckDokumentType.INFOPAKET:
      return 'Infopaket';
    case DruckDokumentType.PKB:
      return 'Policenkontrollblatt';
    case DruckDokumentType.WEWE:
      return 'Einwilligungserklärung_zur_Datenverarbeitung';
    case DruckDokumentType.VORSCHLAG:
      return 'Privatschutz-Vorschlag';
    case DruckDokumentType.JAGDBESTAETIGUNG:
      return 'Jagd-Haftpflichtversicherung-Bestätigung';
  }
}

function appNameToSpartenBezeichnung(app: DruckDokumentSparte): string | undefined {
  const appToSpartenbezechnung = {
    [DruckDokumentSparte.VHV]: 'Hausratversicherung',
    [DruckDokumentSparte.VWG]: 'Wohngebäudeversicherung',
    [DruckDokumentSparte.PH]: 'Haftpflichtversicherung',
    [DruckDokumentSparte.RS]: 'Rechtsschutzversicherung',
    [DruckDokumentSparte.RS_VERMIETUNG]: 'Rechtsschutzversicherung',
    [DruckDokumentSparte.URI]: 'Unfallversicherung',
    [DruckDokumentSparte.THH_HUND]: 'Tierhalter-Haftpflicht (Hund)',
    [DruckDokumentSparte.THH_PFERD]: 'Tierhalter-Haftpflicht (Pferd)',
    [DruckDokumentSparte.TKV]: 'Tierkrankenversicherung',
    [DruckDokumentSparte.GEGE]: 'Hausratversicherung–Gegenstandsschutz',
  };
  const spartenBezeichnung = (appToSpartenbezechnung as Partial<Record<DruckDokumentSparte, string>>)[app];
  if (spartenBezeichnung) {
    return spartenBezeichnung;
  } else {
    throw new Error('Unknown DruckDokumentSparte!');
  }
}

export function generateDokumentname({
  type,
  spartenName,
  vorname,
  adelsbez,
  nachname,
}: DokumentnameBestandteile): string {
  const dokumentTypBezeichnung = druckDokumentTypeToBezeichnung(type);
  const spartenBezeichnung = spartenName != null ? appNameToSpartenBezeichnung(spartenName) : null;
  const dokumentenNameBestandteile = [
    dokumentTypBezeichnung,
    spartenBezeichnung,
    vorname,
    adelsbez,
    nachname,
  ];

  if (type === DruckDokumentType.VORSCHLAG) {
    dokumentenNameBestandteile.push(dayjs().format('DD-MM-YYYY'));
  }

  return dokumentenNameBestandteile
    .filter(element => element != null)
    .join('_')
    .concat('.pdf');
}

export function getBeziehungVpZumVn(
  state: IAppState,
  sparteDruckdaten: SparteDruckdaten,
): BeziehungZumVn {
  if (isUnfall(sparteDruckdaten)) {
    return state.beratung.assekuranzPersonenAngaben.find(
      value => value.angebotsId === sparteDruckdaten.angebotsId,
    )?.beziehungZumVn;
  } else {
    return null;
  }
}

function isUnfall(sparteDruckdaten: SparteDruckdaten): boolean {
  return sparteDruckdaten.spartenType === SpartenType.UNFALL;
}
